import React, { useEffect, useRef, useState, useCallback, useId } from 'react';
import { Link } from 'react-router-dom';
import { User, LogOut, ChevronDown, Settings, CreditCard, HelpCircle, Link2 } from 'lucide-react';

interface UserMenuProps {
  email: string;
  username?: string;
  onSignOut: () => void;
}

/**
 * User profile dropdown menu
 *
 * Displays user information and provides access to account actions.
 * Fully accessible with keyboard navigation and ARIA attributes.
 */
export const UserMenu: React.FC<UserMenuProps> = ({ email, username, onSignOut }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuItemsRef = useRef<(HTMLAnchorElement | HTMLButtonElement | null)[]>([]);
  const menuId = useId();

  const menuItems = [
    { type: 'link' as const, to: '/app/settings', label: 'Settings', icon: Settings },
    { type: 'link' as const, to: '/app/brokerage', label: 'Brokerage', icon: Link2 },
    { type: 'link' as const, to: '/app/pricing', label: 'Subscription', icon: CreditCard },
    { type: 'link' as const, to: '/app/settings?tab=support', label: 'Help & Support', icon: HelpCircle },
    { type: 'button' as const, label: 'Sign out', icon: LogOut, action: onSignOut },
  ];

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setFocusedIndex(-1);
    triggerRef.current?.focus();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeMenu]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setIsOpen(true);
          setFocusedIndex(0);
        }
        return;
      }

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          closeMenu();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => (prev + 1) % menuItems.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => (prev - 1 + menuItems.length) % menuItems.length);
          break;
        case 'Home':
          e.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setFocusedIndex(menuItems.length - 1);
          break;
        case 'Tab':
          closeMenu();
          break;
      }
    },
    [isOpen, menuItems.length, closeMenu]
  );

  // Focus management
  useEffect(() => {
    if (isOpen && focusedIndex >= 0) {
      menuItemsRef.current[focusedIndex]?.focus();
    }
  }, [isOpen, focusedIndex]);

  const displayName = username || email.split('@')[0];
  const truncatedEmail = email.length > 25 ? `${email.substring(0, 22)}...` : email;

  return (
    <div className="relative" ref={menuRef} onKeyDown={handleKeyDown}>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) setFocusedIndex(0);
        }}
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold hover:bg-slate-800/60 transition-all duration-200 text-slate-300 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-st-primary focus-visible:ring-offset-2 focus-visible:ring-offset-st-dark"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-controls={menuId}
        aria-label={`User menu for ${displayName}`}
      >
        <div className="w-7 h-7 rounded-full bg-st-primary/20 border border-st-primary/40 flex items-center justify-center">
          <User size={14} className="text-st-primary" aria-hidden="true" />
        </div>
        <span className="hidden sm:inline-block max-w-[120px] truncate text-xs tracking-wide uppercase">
          {displayName}
        </span>
        <ChevronDown
          size={14}
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          id={menuId}
          role="menu"
          aria-label="User account menu"
          className="absolute right-0 mt-2 w-64 bg-st-card border border-slate-800 rounded-lg shadow-xl overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200"
        >
          {/* User Info Section */}
          <div className="px-4 py-3 border-b border-slate-800 bg-gradient-to-b from-[#0b0d11] to-st-card">
            <p className="text-xs font-bold tracking-widest uppercase text-st-primary mb-1">
              Signed in as
            </p>
            <p className="text-sm font-semibold text-white truncate">{displayName}</p>
            <p className="text-xs text-st-muted mt-0.5 truncate" title={email}>
              {truncatedEmail}
            </p>
          </div>

          {/* Actions Section */}
          <div className="py-2" role="none">
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              const baseClassName =
                'w-full px-4 py-2.5 text-left text-sm font-bold tracking-wide hover:bg-slate-800/60 text-slate-300 hover:text-white transition-colors flex items-center gap-3 focus:outline-none focus:bg-slate-800/60 focus:text-white';

              if (item.type === 'link') {
                return (
                  <Link
                    key={item.label}
                    ref={(el) => {
                      menuItemsRef.current[index] = el;
                    }}
                    to={item.to}
                    role="menuitem"
                    tabIndex={focusedIndex === index ? 0 : -1}
                    onClick={closeMenu}
                    className={baseClassName}
                  >
                    <Icon size={16} aria-hidden="true" />
                    {item.label}
                  </Link>
                );
              }

              return (
                <button
                  key={item.label}
                  ref={(el) => {
                    menuItemsRef.current[index] = el;
                  }}
                  type="button"
                  role="menuitem"
                  tabIndex={focusedIndex === index ? 0 : -1}
                  onClick={() => {
                    closeMenu();
                    item.action?.();
                  }}
                  className={baseClassName}
                >
                  <Icon size={16} aria-hidden="true" />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
